import React, { useState } from "react";
import { FiWind, FiZap, FiSettings } from "react-icons/fi";
import { MiniBar, co2Color } from "./aqmHelpers";

const fanColor = (speed) => {
  if (speed === "High")   return "#ef4444";
  if (speed === "Medium") return "#f59e0b";
  return "#10b981";
};

const ahuColor = (s) => {
  if (s === "Running") return { color: "#10b981", bg: "#d1fae5" };
  if (s === "Standby") return { color: "#3b82f6", bg: "#dbeafe" };
  return { color: "#ef4444", bg: "#fee2e2" };
};

const AqmHvacPanel = ({ d }) => {
  const { hvac } = d;
  const [mode, setMode] = useState(hvac.mode);

  const co2Critical = hvac.zones.filter(z => z.co2 > 1000).length;

  return (
    <div className="aqm-hvac-panel">

      {/* Control header */}
      <div className="aqm-hvac-header-card">
        <div>
          <div className="aqm-section-title"><FiSettings size={15} style={{ marginRight: 6 }} /> HVAC Control Center</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Total Fresh Airflow: <strong style={{ color: "#3b82f6" }}>{hvac.totalFreshAirflow.toLocaleString()} m³/hr</strong> ·
            Energy Saving: <strong style={{ color: "#10b981" }}>{hvac.energySavingPct}%</strong> vs fixed schedule
          </div>
        </div>
        <div className="aqm-hvac-mode-row">
          <span style={{ fontSize: 12, color: "#6b7280" }}>Mode:</span>
          {["Auto","Manual"].map(m => (
            <button key={m} className={`aqm-mode-btn ${mode === m ? "aqm-mode-active" : ""}`} onClick={() => setMode(m)}>{m}</button>
          ))}
          {mode === "Auto" && (
            <span className="aqm-badge" style={{ background: "#d1fae5", color: "#065f46" }}>CO₂ Auto-Trigger: ON</span>
          )}
        </div>
      </div>

      {/* CO₂ trigger status */}
      {co2Critical > 0 && (
        <div className="aqm-hvac-alert-banner">
          ⚠ {co2Critical} zone{co2Critical > 1 ? "s" : ""} with CO₂ &gt; 1000 ppm — Auto-ventilation triggered. Fresh air dampers at maximum.
        </div>
      )}

      {/* Zone cards */}
      <div className="aqm-section-title">HVAC Zones</div>
      <div className="aqm-hvac-zone-grid">
        {hvac.zones.map(z => {
          const co2c = co2Color(z.co2);
          const fc   = fanColor(z.fanSpeed);
          const ahuc = ahuColor(z.ahuStatus);
          const isCritical = z.co2 > 1000;
          return (
            <div key={z.id} className={`aqm-zone-card ${isCritical ? "aqm-zone-critical" : ""}`} style={{ borderTopColor: co2c }}>
              <div className="aqm-zc-header">
                <div>
                  <div className="aqm-zc-id">{z.id}</div>
                  <div className="aqm-zc-name">{z.name}</div>
                  <div className="aqm-zc-floor">Floor: {z.floor}</div>
                </div>
                <span className="aqm-badge" style={{ background: ahuc.bg, color: ahuc.color }}>{z.ahuStatus}</span>
              </div>

              <div className="aqm-zc-co2">
                <div style={{ color: co2c, fontWeight: 800, fontSize: 22 }}>{z.co2} <span style={{ fontSize: 13, fontWeight: 500 }}>ppm</span></div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>CO₂ level</div>
              </div>

              <div style={{ margin: "8px 0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#6b7280", marginBottom: 3 }}>
                  <span>Damper Open</span><span style={{ color: co2c, fontWeight: 600 }}>{z.damperPct}%</span>
                </div>
                <MiniBar value={z.damperPct} max={100} color={co2c} height={8} />
              </div>

              <div className="aqm-zc-stats">
                <div className="aqm-zc-stat">
                  <FiWind size={12} color={fc} />
                  <span style={{ color: fc, fontWeight: 600, fontSize: 12 }}>{z.fanSpeed}</span>
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>Fan</span>
                </div>
                <div className="aqm-zc-stat">
                  <FiZap size={12} color="#3b82f6" />
                  <span style={{ fontWeight: 600, fontSize: 12, color: "#374151" }}>{z.airflow} m³/h</span>
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>Airflow</span>
                </div>
              </div>

              <div className="aqm-zc-trigger">
                Trigger: <span style={{ color: isCritical ? "#ef4444" : "#10b981", fontWeight: 600 }}>{z.trigger}</span>
              </div>

              {mode === "Manual" && (
                <div className="aqm-zc-controls">
                  <button className="aqm-ctrl-btn aqm-ctrl-blue">+ Damper</button>
                  <button className="aqm-ctrl-btn aqm-ctrl-gray">– Damper</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="aqm-hvac-summary">
        <div className="aqm-hvac-sum-card" style={{ borderTopColor: "#10b981" }}>
          <div className="aqm-hsc-val" style={{ color: "#10b981" }}>{hvac.zones.filter(z => z.ahuStatus === "Running").length}</div>
          <div className="aqm-hsc-lbl">AHUs Running</div>
        </div>
        <div className="aqm-hvac-sum-card" style={{ borderTopColor: "#ef4444" }}>
          <div className="aqm-hsc-val" style={{ color: "#ef4444" }}>{co2Critical}</div>
          <div className="aqm-hsc-lbl">High CO₂ Zones</div>
        </div>
        <div className="aqm-hvac-sum-card" style={{ borderTopColor: "#3b82f6" }}>
          <div className="aqm-hsc-val" style={{ color: "#3b82f6" }}>{hvac.totalFreshAirflow.toLocaleString()}</div>
          <div className="aqm-hsc-lbl">Total Airflow m³/h</div>
        </div>
        <div className="aqm-hvac-sum-card" style={{ borderTopColor: "#10b981" }}>
          <div className="aqm-hsc-val" style={{ color: "#10b981" }}>{hvac.energySavingPct}%</div>
          <div className="aqm-hsc-lbl">Energy Saved</div>
        </div>
      </div>

      <div className="aqm-ai-strip">
        🤖 AI: Zones Z3 & Z4 (Meeting Rooms, Open Office) have CO₂ above 1000 ppm. Increasing damper to 95% and fan to High. Estimated CO₂ reduction: 280–350 ppm within 25 minutes.
        {mode === "Auto" ? " Auto mode active — no manual action required." : " Switch to Auto mode for automatic CO₂-triggered ventilation."}
      </div>
    </div>
  );
};

export default AqmHvacPanel;
